import type * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useDiscardAutomationDraftMutation } from '../../../../queries/Automation';

interface UseDiscardDraftOptions {
	templateId: string | undefined;
	/** The automation template — used to determine if there are draft changes to discard */
	template?: Api.IAutomationTemplate;
	onDiscardSuccess?: () => void;
	onDiscardError?: (error: Api.IOperationResultNoValue) => void;
	/** Optional impersonation context for admin operations */
	impersonationContext?: Api.IImpersonationContext;
}

interface UseDiscardDraftReturn {
	discard: () => Promise<void>;
	isDiscarding: boolean;
	canDiscard: boolean;
}

/**
 * Determines whether the template has draft changes that can be discarded.
 *
 * Discard is only meaningful when both a draft AND a published version exist.
 * - If only a draft exists (never published), there's nothing to revert to.
 * - If only a published version exists (no draft), there's nothing to discard.
 */
function hasDraftChanges(template?: Api.IAutomationTemplate): boolean {
	if (!template) {
		return false;
	}

	const hasDraft = !!(template.draft?.steps || template.draft?.trigger);
	const hasPublished = !!(template.published?.steps || template.published?.trigger);

	return hasDraft && hasPublished;
}

export function useDiscardDraft({
	templateId,
	template,
	onDiscardSuccess,
	onDiscardError,
	impersonationContext,
}: UseDiscardDraftOptions): UseDiscardDraftReturn {
	const discardDraftMutation = useDiscardAutomationDraftMutation({
		onSuccess: () => {
			onDiscardSuccess?.();
		},
		onError: error => {
			onDiscardError?.(error);
		},
	});

	const discard = useCallback(async () => {
		if (!templateId) {
			return;
		}

		await discardDraftMutation.mutateAsync({
			id: templateId,
			impersonationContext,
		});
	}, [templateId, discardDraftMutation, impersonationContext]);

	const canDiscard = Boolean(templateId) && hasDraftChanges(template);

	return {
		discard,
		isDiscarding: discardDraftMutation.isPending,
		canDiscard,
	};
}
